import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { useChainId } from 'wagmi'

import { isEmpty } from 'lodash'

import { ErrorsList, Token } from 'types/entities'

import { useAssetBalance } from 'hooks/token'

import {
  useToken,
  useRecipientsList,
  useIsAirdropPending,
  useSetRecipientsList,
  useSetAirdropChainId,
  useSetExceptionsList,
  useClearAirdropParams,
} from 'state/airdrop/hooks'
import { StoredAirdropList } from 'state/airdrop/types'

import { errors } from 'constants/errors'
import { TokenSubLinks, getTokenSubLink } from 'constants/link'

import { arrayToList } from 'utils/helpers'
import { parseToTokenAirdropList } from 'utils/editorParser'
import { errorParser, parseErrorToUserReadableMessage } from 'utils/errors'
import { findDuplicates, DuplicatesInfo, mergeDuplicates, validateRecipientsList } from 'utils/checkRecipients'

import { ActionButton } from 'components/features'
import { Button, Notification, Tooltip } from 'components/ui'

import { SearchBar } from './SearchBar'
import { RecipientsEditor } from './RecipientsEditor'

import { Section, ErrorsWrap, ButtonsWrap, ActionButtonWrap } from './Style'

enum BtnText {
  CONTINUE = '_continue',
  MERGE = '_merge-duplicates',
  WITHOUT_MERGE = '_proceed-without-merging',
}

export function MainForm() {
  const token = useToken()
  const chainId = useChainId()
  const recipientsList = useRecipientsList()

  const isAirdropPending = useIsAirdropPending()

  const setAirdropChainId = useSetAirdropChainId()
  const setRecipientsList = useSetRecipientsList()
  const setExceptionsList = useSetExceptionsList()

  const clearAirdropParams = useClearAirdropParams()

  const navigate = useNavigate()
  const getAssetBalance = useAssetBalance()

  const [isLoading, setIsLoading] = useState(false)
  const [editorData, setEditorData] = useState(arrayToList(recipientsList))

  const [pageError, setPageError] = useState('')
  const [tokenErrors, setTokenErrors] = useState<string[]>([])
  const [duplicates, setDuplicates] = useState<DuplicatesInfo>({})
  const [duplicateError, setDuplicateError] = useState<ErrorsList>([])
  const [editorErrorList, setEditorErrorList] = useState<ErrorsList>([])

  useEffect(() => {
    clearErrors()
  }, [token?.address])

  const clearErrors = () => {
    setPageError('')
    setTokenErrors([])
    setEditorErrorList([])
    setExceptionsList([])
  }

  const onEditorChange = (value: string) => {
    setEditorData(value)
  }

  const handleMergeDuplicates = () => {
    const mergedRecipients = mergeDuplicates(recipientsList, duplicates)

    setDuplicateError([])

    onEditorChange(arrayToList(mergedRecipients))

    setRecipientsList(mergedRecipients)

    setDuplicates({})
  }

  const onSwitchToNextPage = () => {
    clearAirdropParams()

    setAirdropChainId(chainId)
    navigate(getTokenSubLink(TokenSubLinks.APPROVAL))
  }

  const updateErrors = (_errorsList: string[], error: string) => {
    return !_errorsList.includes(error) ? _errorsList.concat(error) : _errorsList
  }

  const checkIsBalanceEnough = async (token: Token, totalTokens: bigint) => {
    try {
      const tokenBalance = await getAssetBalance(token.address)

      if (BigInt(tokenBalance) < totalTokens) {
        setTokenErrors(updateErrors(tokenErrors, errors.validation.INSUFFICIENT_TOKEN_BALANCE))
        return false
      }
      return true
    } catch (err) {
      setTokenErrors(updateErrors(tokenErrors, errorParser(err.message)))
      return false
    }
  }

  const handleValidate = async (_recipientsList: StoredAirdropList) => {
    clearErrors()

    try {
      setIsLoading(true)
      if (!token?.address) {
        setTokenErrors(updateErrors(tokenErrors, errors.validation.TOKEN_IS_REQUIRED))
        return false
      }

      if (_recipientsList.length < 2) {
        setTokenErrors(updateErrors(tokenErrors, errors.validation.RECIPIENT_LIST_IS_REQUIRED))
        return false
      }

      const {
        totalTokens,
        errors: _errors,
        recipientsListWithoutEns,
      } = await validateRecipientsList(_recipientsList, token)

      setEditorErrorList(_errors)

      const isBalanceEnough = await checkIsBalanceEnough(token, totalTokens)

      if (!isBalanceEnough) {
        setTokenErrors(updateErrors(tokenErrors, errors.validation.INSUFFICIENT_TOKEN_BALANCE))
      }

      const { hasDuplicates, duplicateListErrors, duplicatesInfo } = findDuplicates(_recipientsList)

      setDuplicates(duplicatesInfo)
      setDuplicateError(duplicateListErrors)

      setRecipientsList(recipientsListWithoutEns)

      return {
        validForContinue: !hasDuplicates && isBalanceEnough && !_errors.length,
        validForNoMerge: isBalanceEnough && !_errors.length,
      }
    } catch (err) {
      setPageError(parseErrorToUserReadableMessage(err))
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const buttonAction = async () => {
    const data = parseToTokenAirdropList(editorData)
    const validation = await handleValidate(data)

    const _validation = validation && (isDuplicates ? validation.validForNoMerge : validation.validForContinue)

    if (_validation) {
      onSwitchToNextPage()
    }
  }

  const isDuplicates = useMemo(() => {
    return !isEmpty(duplicates)
  }, [duplicates])

  const buttonType = useMemo(() => {
    return isDuplicates ? 'outline' : 'default'
  }, [isDuplicates])

  const buttonText = useMemo(() => {
    return isDuplicates ? BtnText.WITHOUT_MERGE : BtnText.CONTINUE
  }, [isDuplicates])

  const isActionDisabled = useMemo(
    () => (recipientsList.length < 2 && !token?.address) || isAirdropPending,
    [recipientsList.length, token?.address, isAirdropPending],
  )

  return (
    <Section>
      {isAirdropPending && (
        <Notification type='warning' margin='0 auto 1.6rem' message={errors.wallet.ALREADY_PROCESSING} />
      )}

      <SearchBar label='_token-address' placeholder='select-your-token' />

      <RecipientsEditor
        editorData={editorData}
        onChange={onEditorChange}
        editorErrorList={editorErrorList}
        duplicateErrorList={duplicateError}
      />

      <ErrorsWrap>
        {tokenErrors.length > 0 &&
          tokenErrors.map((err: string, index: number) => <Notification key={index} type='error' message={err} />)}
      </ErrorsWrap>

      <ButtonsWrap isDuplicates={isDuplicates}>
        {isDuplicates && (
          <Button fullSize type='primary' size='large' onClick={handleMergeDuplicates} isLoading={isLoading}>
            {BtnText.MERGE}
          </Button>
        )}

        <ActionButtonWrap id='processingTx'>
          <ActionButton
            type={buttonType}
            text={buttonText}
            isLoading={isLoading}
            loadingText='_loading'
            isDisabled={isActionDisabled}
            handleAction={buttonAction}
          />
        </ActionButtonWrap>

        {isAirdropPending && (
          <Tooltip id='processingTx' place='top' effect='solid'>
            Previous sending is still processing! Please wait until it is done
          </Tooltip>
        )}
      </ButtonsWrap>

      {pageError && <Notification type='error' message={pageError} />}
    </Section>
  )
}
